import React, { useEffect, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';

const GLOBAL_OFFER_BLOCK_LIST_PAGE_SIZE = 25;
type Props = Pick<
  ChannelMarkupTesterPanelProps,
  | 'clientInitUpdatedAt'
  | 'clientInitRefreshMessage'
  | 'clientInitRefreshError'
  | 'clientInitConfig'
  | 'isClientInitFetching'
  | 'clientInitMode'
  | 'setClientInitMode'
  | 'tenantBrand'
  | 'handleRefreshClientInit'
>;

export default function ChannelMarkupClientInitSection({
  clientInitUpdatedAt,
  clientInitRefreshMessage,
  clientInitRefreshError,
  clientInitConfig,
  isClientInitFetching,
  clientInitMode,
  setClientInitMode,
  tenantBrand,
  handleRefreshClientInit,
}: Props) {
  const globalOfferIdBlockList = clientInitConfig?.channelMarkupConfig.globalOfferIdBlockList ?? [];
  const [globalOfferBlockListPage, setGlobalOfferBlockListPage] = useState(1);
  const globalOfferBlockListPageCount = Math.ceil(globalOfferIdBlockList.length / GLOBAL_OFFER_BLOCK_LIST_PAGE_SIZE);
  const visibleGlobalOfferIds = globalOfferIdBlockList.slice(
    (globalOfferBlockListPage - 1) * GLOBAL_OFFER_BLOCK_LIST_PAGE_SIZE,
    globalOfferBlockListPage * GLOBAL_OFFER_BLOCK_LIST_PAGE_SIZE,
  );

  useEffect(() => {
    setGlobalOfferBlockListPage((currentPage) => Math.min(currentPage, Math.max(globalOfferBlockListPageCount, 1)));
  }, [globalOfferBlockListPageCount]);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            1. Client init
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Load the active client init snapshot before evaluating anything in general mode.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            label="Mode"
            value={clientInitMode}
            onChange={(event) => setClientInitMode(event.target.value === 'compt' ? 'compt' : 'default')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="compt">Compt</MenuItem>
          </TextField>
          <TextField
            label="client-init-refresh-at"
            value={clientInitUpdatedAt ? dayjs(clientInitUpdatedAt).format('YYYY-MM-DD HH:mm:ss') : 'n/a'}
            InputProps={{ readOnly: true }}
            sx={{ minWidth: 220 }}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="text" onClick={() => void handleRefreshClientInit()} disabled={isClientInitFetching}>
              {isClientInitFetching ? 'Refreshing...' : `Refresh ${tenantBrand} client init`}
            </Button>
            {clientInitConfig && (
              <DebugModal
                type="generic"
                title="Client init loaded"
                data={clientInitConfig}
                hideIcon
                buttonProps={{ size: 'small', variant: 'text' }}
              >
                Inspect client init
              </DebugModal>
            )}
          </Stack>
          {clientInitRefreshMessage && (
            <Typography variant="body2" color="text.secondary">
              {clientInitRefreshMessage}
            </Typography>
          )}
          {clientInitRefreshError && (
            <Alert severity="error" variant="outlined">
              {clientInitRefreshError}
            </Alert>
          )}
        </Stack>
        {clientInitConfig && (
          <Accordion defaultExpanded={false} disableGutters>
            <AccordionSummary aria-controls="global-offer-block-list-content" id="global-offer-block-list-summary">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Global offer block list
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {globalOfferIdBlockList.length} blocked offer{globalOfferIdBlockList.length === 1 ? '' : 's'} from
                  this client-init snapshot
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {globalOfferIdBlockList.length > 0 ? (
                <Stack spacing={1}>
                  <List dense disablePadding aria-label="Global offer block list">
                    {visibleGlobalOfferIds.map((offerId, index) => (
                      <ListItemButton key={`${offerId}-${index}`} disableRipple>
                        <ListItemText primary={offerId} />
                      </ListItemButton>
                    ))}
                  </List>
                  {globalOfferBlockListPageCount > 1 && (
                    <Pagination
                      count={globalOfferBlockListPageCount}
                      page={globalOfferBlockListPage}
                      onChange={(_event, page) => setGlobalOfferBlockListPage(page)}
                      shape="rounded"
                      size="small"
                    />
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No offers are globally blocked in this client-init snapshot.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>
    </Box>
  );
}
